import { defineStore } from 'pinia'
import type { ILabAdminInvite } from '@team-milva/lab-models/invite'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { orderBy } from 'lodash-es'
import { adminApi } from '@/globals/typescript/utils/api/adminClient'

export const useInviteStore = defineStore('invite', {
  state: () => ({
    allActiveInvites: [] as ILabAdminInvite[],
    unsubscribeAllActiveInvites: null as (() => void) | null,
  }),

  actions: {
    getAllActiveInvites(): Promise<ILabAdminInvite[]> {
      if (this.allActiveInvitesLoaded) {
        return Promise.resolve(this.allActiveInvites)
      }

      return new Promise((resolve) => {
        let resolved = false

        const q = query(
          collection(db, 'Apps', '-lab-admin-', 'Invites'),
          where('status', '==', 'pending'),
          where('timestamps.expiresAt', '>', Date.now())
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.allActiveInvites = querySnapshot.docs.map(
            (doc) => doc.data() as ILabAdminInvite
          )
          this.unsubscribeAllActiveInvites = unsubscribe

          const sortedInvites = orderBy(
            this.allActiveInvites,
            ['email', 'timestamps.createdAt'],
            ['asc', 'desc']
          )
          this.allActiveInvites = sortedInvites

          // Only resolve once
          if (!resolved) {
            resolved = true
            resolve(this.allActiveInvites)
          }
        })
      })
    },

    async cancelInvite(inviteID: string): Promise<void> {
      const result = await adminApi('post', '/invites/revoke', {
        body: {
          inviteID,
        },
      })
      if (!result.success) throw new Error(result.code)
    },

    resetStore(): void {
      this.allActiveInvites = []

      if (this.unsubscribeAllActiveInvites) {
        this.unsubscribeAllActiveInvites()
        this.unsubscribeAllActiveInvites = null
      }
    },
  },

  getters: {
    allActiveInvitesLoaded(state): boolean {
      return state.allActiveInvites.length > 0
    },
  },
})

